import { Button, Flex, FormItemsGroup, Input, notify, Space, Spacing, Switch } from '@billy/design-system'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { queryClient } from '../../../../../../../../config/queryClient'
import { QueryKeys } from '../../../../../../../../enums/queryKeys'
import { useForm, useFormContext } from '../../../../../../../../hooks'
import { APIError } from '../../../../../../../../utils'
import { getErrorMessage } from '../../../../../../../../utils/getErrorMessage'
import { useVatDeclaration } from '../../../../../../context/vatDeclarationContext'
import { useSubmitVatDeclaration } from '../../../../../../hooks/useSubmitVatDeclaration'
import { SubmitVatDeclarationPayload } from '../../../../../../query-api'
import { getDefaultValues, getVatIntegrationFormSchema, VatIntegrationForm, VatIntegrationFormSchema } from './formData'
import * as Styled from './styles'

interface IntegrationFormProps {
  onNext: () => void
  onCancel: () => void
}

export const IntegrationForm = ({ onNext, onCancel }: IntegrationFormProps) => {
  const { id, periodText } = useVatDeclaration()
  const { organization } = useUserOrganization()
  const { t } = useTranslation()
  const { FormItem } = useFormContext<VatIntegrationFormSchema>()
  const { Form, handleSubmit, setError, formState, watch } = useForm<VatIntegrationFormSchema>({
    defaultValues: useMemo(() => getDefaultValues(organization), [organization]),
    validationSchema: useMemo(() => getVatIntegrationFormSchema(t), [t]),
  })

  const { contactInitials, contactSurname, taxIdentificationNumber, authorization } = watch()

  const hasTaxIdError = !!formState.errors?.taxIdentificationNumber
  const isButtonDisabled =
    !contactInitials || !contactSurname || !taxIdentificationNumber || !authorization || hasTaxIdError

  const handleSubmitVatDeclarationError = useCallback(
    (error?: APIError) => {
      const propertiesErrors = error?.validationErrors?.record?.attributes

      if (propertiesErrors) {
        for (const [key, value] of Object.entries(propertiesErrors) as [keyof VatIntegrationForm, string][]) {
          setError(key, { type: 'required', message: value })
        }
      } else {
        const message = getErrorMessage(error, 'record') || t('error_saving')
        notify({ id: 'submitVatDeclarationError', message, variant: 'error' })
      }
    },
    [setError, t],
  )

  const handleSubmitVatDeclarationSuccess = useCallback(async () => {
    await queryClient.invalidateQueries([QueryKeys.VatDeclarations])
    await queryClient.invalidateQueries([QueryKeys.VatDeclarationSubmission, id, organization?.id], {
      refetchActive: true,
    })
    onNext()
  }, [id, onNext, organization?.id])

  const { mutate, isLoading } = useSubmitVatDeclaration({
    onError: handleSubmitVatDeclarationError,
    onSuccess: handleSubmitVatDeclarationSuccess,
  })

  const handleSubmitForm = useCallback(
    (values: VatIntegrationForm) => {
      const { contactInitials, contactSurname, contactSurnamePrefix, taxIdentificationNumber } = values
      const formattedTaxIdentificationNumber = taxIdentificationNumber?.replace(/^NL/i, '')

      const payload: SubmitVatDeclarationPayload = {
        contactInitials,
        contactSurname,
        taxIdentificationNumber: formattedTaxIdentificationNumber,
      }

      if (contactSurnamePrefix) {
        payload.contactSurnamePrefix = contactSurnamePrefix
      }

      mutate({ payload, salesTaxReturnId: id })
    },
    [id, mutate],
  )

  const submitForm = useCallback(() => {
    handleSubmit(handleSubmitForm)()
  }, [handleSubmit, handleSubmitForm])

  return (
    <Form>
      <FormItemsGroup itemsInRow={2}>
        <FormItem
          label={t('vat_declarations.integration_form.form.initials') + ' *'}
          name="contactInitials"
          render={({ field }) => <Input {...field} required placeholder="J." maxLength={1} />}
        />
        <FormItem
          label={t('vat_declarations.integration_form.form.surname_prefix')}
          name="contactSurnamePrefix"
          render={({ field }) => <Input {...field} placeholder="van" maxLength={10} />}
        />
      </FormItemsGroup>

      <Space size="s16" />

      <FormItem
        label={t('vat_declarations.integration_form.form.lastname') + ' *'}
        name="contactSurname"
        render={({ field }) => <Input {...field} required placeholder="Janssen" />}
      />

      <Space size="s24" />

      <FormItem
        label={t('vat_declarations.integration_form.form.vatNo') + ' *'}
        name="taxIdentificationNumber"
        render={({ field }) => <Input {...field} required placeholder="123456789B01" />}
      />

      <Space size="s24" />

      <FormItem
        name="authorization"
        render={({ field: { value, ...rest } }) => (
          <Switch checked={value} {...rest}>
            {t('vat_declarations.integration_form.form.authorization', {
              period: periodText,
              company: organization?.name,
            })}
          </Switch>
        )}
      />

      <Styled.IntegrationFormButtonsWrapper>
        <Flex alignContent="center" flexDirection="column" gap={Spacing.S16}>
          <Button
            onClick={submitForm}
            fullWidth
            disabled={isButtonDisabled || isLoading || !!formState.errors?.taxIdentificationNumber}
            loading={isLoading}
          >
            {t('vat_declarations.integration_form.form.submit')}
          </Button>

          <Button variant="text" onClick={onCancel}>
            {t('cancel')}
          </Button>
        </Flex>
      </Styled.IntegrationFormButtonsWrapper>
    </Form>
  )
}
